/**
 * Workflow Templates catalogue.
 *
 * Standalone catalogue view — reached from the "View all →" link on the
 * Trust Workflows landing page or the "Create Workflow" CTA.
 *
 * Sprint 6 scope: presentational scaffold. Template designer is a P1
 * follow-up under Trust Workflows.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, KeyRound, Fingerprint, ShieldCheck, GraduationCap, BookOpen, FileCheck2, Dice5 } from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const TEMPLATES = [
  { id: "customer-onboarding",    name: "Customer Onboarding",    icon: KeyRound,      steps: ["Consent", "Identity", "KYC", "Product Understanding", "Acceptance"], category: "Financial services" },
  { id: "investor-onboarding",    name: "Investor Onboarding",    icon: FileCheck2,    steps: ["Consent", "Identity", "KYC", "Affordability", "Suitability", "Acceptance"], category: "Wealth" },
  { id: "gaming-registration",    name: "Gaming Registration",    icon: Dice5,         steps: ["Consent", "Identity", "Age Gate", "RG Threshold", "Acceptance"], category: "Gaming" },
  { id: "product-understanding",  name: "Product Understanding",  icon: BookOpen,      steps: ["Learning", "Assessment", "Evidence Emission"], category: "Cross-sector" },
  { id: "identity-verification",  name: "Identity Verification",  icon: Fingerprint,   steps: ["Consent", "PDV Fetch", "Vendor Verify"], category: "Cross-sector" },
  { id: "kyc-verification",       name: "KYC Verification",       icon: ShieldCheck,   steps: ["Consent", "Identity", "Sanctions", "PEP"], category: "Cross-sector" },
  { id: "learning-assignment",    name: "Learning Assignment",    icon: GraduationCap, steps: ["Assign Lesson", "Consumer Completes", "Assessment"], category: "Education" },
];

export default function WorkflowTemplatesPage() {
  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-6" data-testid="workflow-templates-page">
      <Link to="/capability/trust-workflows" data-testid="back-to-trust-workflows" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" /> Trust Workflows
      </Link>

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
            Trust Workflows · Templates
          </div>
          <h1 className="font-display text-3xl font-semibold" style={{ color: NAVY }}>Workflow Templates</h1>
          <p className="text-[15px] text-slate-600 max-w-3xl mt-2 leading-relaxed">
            Reusable, versioned templates composed from workflow primitives. Publish once, run everywhere — with per-tenant configuration overlays.
          </p>
        </div>
        <button
          data-testid="new-template-btn"
          disabled
          className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white opacity-60 cursor-not-allowed"
          style={{ background: NAVY }}
          title="Workflow Designer ships in Phase 1.5"
        >
          <Plus className="w-4 h-4" /> New Template
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="templates-grid">
        {TEMPLATES.map((t) => {
          const Icon = t.icon;
          return (
            <div
              key={t.id}
              data-testid={`template-${t.id}`}
              className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 space-y-3"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${GOLD}22`, color: GOLD }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-semibold" style={{ color: NAVY }}>{t.name}</div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 mt-0.5">{t.category}</div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {t.steps.map((s, i) => (
                  <span key={i} className="inline-flex items-center">
                    <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded" style={{ background: "#F1F5F9", color: "#334155" }}>
                      {s}
                    </span>
                    {i < t.steps.length - 1 && <span className="text-slate-300 mx-1 text-[10px]">→</span>}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <section
        data-testid="templates-phase1-note"
        className="rounded-2xl border-2 border-dashed border-amber-200 bg-amber-50 p-5"
      >
        <div className="text-[13px] leading-relaxed" style={{ color: "#78350F" }}>
          <strong>Coming in Phase 1.5:</strong> a drag-and-drop Workflow Designer where these templates are composed, versioned, and published per tenant. Today&apos;s flows (Consent, KYC, Trust Package) already implement the primitives — the Designer is the visual composer over them.
        </div>
      </section>
    </div>
  );
}
